import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { Layout, PageHeader } from "@/components/Layout";
import { Seo } from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { FileText, Download, MailWarning, Package, Search, Info, Building2, Share2, QrCode } from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import heroImg from "@/assets/catalog-hero.jpg";

const API = process.env.REACT_APP_BACKEND_URL;
const GROUPS = ["controladores", "sondas", "bombas"];

export default function ProductsCatalog() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [sortBy, setSortBy] = useState("recommended");
  const [category, setCategory] = useState(() => {
    const c = new URLSearchParams(window.location.search).get("category");
    return ["controladores", "sondas", "bombas"].includes(c) ? c : "all";
  });
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/products?lang=${lang}`);
      setProducts(data);
    } finally {
      setLoading(false);
    }
  }, [lang]);
  useEffect(() => { load(); }, [load]);

  const resend = async () => {
    try {
      await api.post("/auth/resend-verification");
      toast.success(t("verify.resent"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    }
  };

  const downloadManual = async (pid, mid, filename, lang) => {
    try {
      const res = await api.get(`/manuals/${pid}/${mid}/download?lang=${lang || ""}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = filename; document.body.appendChild(a); a.click();
      document.body.removeChild(a); URL.revokeObjectURL(url);
    } catch (e) {
      const msg = e?.response?.status === 403 ? t("verify.needed") : formatApiErrorDetail(e);
      toast.error(msg);
    }
  };

  const quickView = (p) => {
    const m = (p.manuals || [])[0];
    if (!m) return;
    const langs = m.langs || [];
    const lg = langs.includes(lang) ? lang : (langs[0] || lang);
    downloadManual(p.id, m.id, (m.filenames && m.filenames[lg]) || `${m.title}.pdf`, lg);
  };

  const term = q.trim().toLowerCase();
  const filtered = useMemo(() => {
    if (!term) return products;
    return products.filter((p) =>
      (p.name || "").toLowerCase().includes(term) ||
      (p.description || "").toLowerCase().includes(term) ||
      (p.reference || "").toLowerCase().includes(term));
  }, [products, term]);

  const sortList = (arr) => {
    if (sortBy === "az") return [...arr].sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    if (sortBy === "new") return [...arr].sort((a, b) => (b.is_new ? 1 : 0) - (a.is_new ? 1 : 0));
    return arr;
  };
  const imgUrl = (pid, iid) => `${API}/api/products/${pid}/image/${iid}`;

  const shareProduct = async (p) => {
    const url = `${window.location.origin}/p/${p.id}`;
    try {
      if (navigator.share) await navigator.share({ title: p.name, url });
      else { await navigator.clipboard.writeText(url); toast.success(t("products.shareCopied")); }
    } catch { /* cancelled */ }
  };

  const ProductCard = ({ p, showGroup }) => (
    <div className="group flex flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl" data-testid={`product-${p.id}`}>
      <button type="button" onClick={() => setSelected(p)} data-testid={`open-${p.id}`}
        className="relative flex aspect-[4/3] items-center justify-center overflow-hidden border-b border-slate-100 bg-white p-4 transition-colors group-hover:bg-slate-50">
        <div className="absolute left-2.5 top-2.5 z-10 flex flex-col items-start gap-1.5">
          {p.is_new && <span className="rounded-full border border-emerald-500/20 bg-emerald-500/10 px-2.5 py-0.5 text-xs font-medium text-emerald-600" data-testid={`badge-new-${p.id}`}>{t("products.badgeNew")}</span>}
          {p.featured && <span className="rounded-full border border-amber-500/20 bg-amber-500/10 px-2.5 py-0.5 text-xs font-medium text-amber-600" data-testid={`badge-featured-${p.id}`}>{t("products.badgeFeatured")}</span>}
        </div>
        <span
          role="button" tabIndex={0}
          onClick={(e) => { e.stopPropagation(); shareProduct(p); }}
          onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); shareProduct(p); } }}
          className="absolute right-2.5 top-2.5 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-white/90 text-slate-500 opacity-0 shadow-sm transition-opacity hover:text-blue-600 group-hover:opacity-100"
          data-testid={`card-share-${p.id}`} aria-label={t("products.share")}>
          <Share2 className="h-4 w-4" />
        </span>
        {p.images && p.images.length > 0 ? (
          <img src={imgUrl(p.id, p.images[0].id)} alt={p.name} className="h-full w-full object-contain transition-transform duration-300 group-hover:scale-105" />
        ) : (
          <Package className="h-12 w-12 text-slate-300" />
        )}
      </button>
      <div className="flex flex-1 flex-col p-4">
        {showGroup && <span className="mb-1 w-fit rounded bg-blue-50 px-2 py-0.5 text-[10px] font-medium text-blue-700">{t(`products.group.${p.group}`)}</span>}
        <h3 className="text-sm font-semibold text-slate-800">{p.name}</h3>
        {p.reference && <p className="mt-0.5 font-mono text-xs text-slate-400">{t("products.reference")}: {p.reference}</p>}
        <p className="mt-1.5 line-clamp-2 flex-1 text-sm leading-relaxed text-slate-500">{p.description}</p>
        <div className="mt-3 flex gap-2">
          <Button className="flex-1 justify-center bg-blue-600 text-white hover:bg-blue-700" size="sm" onClick={() => setSelected(p)} data-testid={`details-${p.id}`}>
            <Info className="mr-1.5 h-4 w-4" /> {t("products.details")}
          </Button>
          {p.manuals.length > 0 && (
            <Button variant="outline" size="sm" className="justify-center" onClick={() => quickView(p)} data-testid={`quickview-${p.id}`}>
              <Download className="mr-1.5 h-4 w-4" /> {t("products.quickView")}
            </Button>
          )}
        </div>
        {p.manuals.length === 0 && <p className="mt-2 text-xs text-slate-400">{t("products.noManuals")}</p>}
      </div>
    </div>
  );

  return (
    <Layout>
      <Seo title="Manuales de producto — Dosacloud | Dosatronic" description="Catálogo de controladores, sondas e instrumentación y bombas de Dosatronic con manuales de usuario descargables en PDF." path="/productos" />
      <PageHeader title={t("products.title")} subtitle={t("products.subtitle")}
        actions={(
          <Button asChild variant="outline" size="sm" data-testid="catalog-about-link">
            <Link to="/sobre"><Building2 className="mr-1.5 h-4 w-4" /> {t("products.about")}</Link>
          </Button>
        )} />
      <div className="space-y-6 p-6">
        {user && user.email_verified === false && (
          <div className="flex flex-col gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 sm:flex-row sm:items-center sm:justify-between" data-testid="verify-banner">
            <div className="flex items-start gap-3">
              <MailWarning className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
              <p className="text-sm text-amber-800">{t("verify.banner")}</p>
            </div>
            <Button size="sm" variant="outline" onClick={resend} data-testid="resend-verification">{t("verify.resend")}</Button>
          </div>
        )}

        <div className="relative overflow-hidden rounded-2xl border border-slate-200 shadow-sm" data-testid="catalog-hero">
          <img src={heroImg} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-blue-950/95 via-blue-900/85 to-blue-800/40" />
          <div className="relative px-6 py-10 sm:px-10 sm:py-14 lg:max-w-2xl">
            <span className="inline-flex items-center rounded-full border border-white/25 bg-white/10 px-3 py-1 text-xs font-medium uppercase tracking-[0.15em] text-blue-100 backdrop-blur-sm" data-testid="hero-tag">{t("products.heroTag")}</span>
            <h2 className="mt-4 text-2xl font-semibold leading-tight tracking-tight text-white sm:text-3xl lg:text-4xl" data-testid="hero-title">{t("products.heroCatalogTitle")}</h2>
            <p className="mt-3 max-w-xl text-sm leading-relaxed text-blue-100/90 sm:text-base" data-testid="hero-subtitle">{t("products.heroCatalogSubtitle")}</p>
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm sm:p-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative min-w-[200px] flex-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("products.searchPlaceholder")}
                className="pl-9" data-testid="catalog-search" />
            </div>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-48" data-testid="catalog-sort"><SelectValue placeholder={t("products.sortBy")} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="recommended">{t("products.sortRecommended")}</SelectItem>
                <SelectItem value="new">{t("products.sortNew")}</SelectItem>
                <SelectItem value="az">{t("products.sortAz")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="mt-3 flex flex-wrap gap-2" data-testid="catalog-category-filter-bar">
            {[["all", t("products.allGroupsCat")], ...GROUPS.map((g) => [g, t(`products.group.${g}`)])].map(([val, lab]) => (
              <button key={val} type="button" onClick={() => setCategory(val)}
                data-testid={`catalog-category-chip-${val}`}
                className={`rounded-full px-5 py-2 text-sm font-medium transition-colors ${category === val
                  ? "bg-blue-600 text-white shadow-md shadow-blue-500/20"
                  : "border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"}`}>
                {lab}
              </button>
            ))}
          </div>
        </div>

        {(() => {
          const list = sortList(filtered.filter((p) => category === "all" || p.group === category));
          return (
            <div data-testid="catalog-results">
              <p className="mb-3 text-sm text-slate-400" data-testid="results-count">{list.length} {t("products.productsWord")}</p>
              {!loading && list.length === 0 && (
                <div className="py-16 text-center" data-testid="catalog-empty">
                  <Package className="mx-auto mb-3 h-10 w-10 text-slate-300" />
                  <p className="text-sm text-slate-400">{term ? t("products.noResults") : t("products.empty")}</p>
                </div>
              )}
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {list.map((p) => <ProductCard key={p.id} p={p} showGroup={category === "all"} />)}
              </div>
            </div>
          );
        })()}
      </div>

      <ProductDetailDialog product={selected} onClose={() => setSelected(null)}
        onDownload={downloadManual} imgUrl={imgUrl} t={t}
        products={products} onOpen={(p) => setSelected(p)}
        onOpenGroup={(g) => { setCategory(g); setSelected(null); }} />
    </Layout>
  );
}

const LANG_LABEL = { es: "ES", en: "EN", de: "DE" };

const ytEmbed = (url) => {
  if (!url) return null;
  const m = url.match(/(?:youtu\.be\/|[?&]v=|embed\/|shorts\/)([\w-]{11})/);
  return m ? `https://www.youtube.com/embed/${m[1]}` : null;
};

function QrShare({ url, name, t }) {
  const downloadPng = () => {
    const canvas = document.getElementById("qr-detail-hires");
    if (!canvas) return;
    const a = document.createElement("a");
    a.href = canvas.toDataURL("image/png");
    a.download = `${(name || "producto").replace(/\s+/g, "-").toLowerCase()}-qr.png`;
    a.click();
  };
  return (
    <div className="mt-4 flex items-center gap-4 rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="detail-qr">
      <div className="rounded-md border border-slate-200 bg-white p-1.5">
        <QRCodeCanvas value={url} size={92} level="M" includeMargin={false} fgColor="#1e3a8a" />
      </div>
      <div style={{ display: "none" }} aria-hidden="true">
        <QRCodeCanvas id="qr-detail-hires" value={url} size={640} level="M" includeMargin={true} fgColor="#1e3a8a" />
      </div>
      <div className="flex-1">
        <p className="flex items-center gap-1.5 text-xs font-medium text-slate-600"><QrCode className="h-3.5 w-3.5 text-blue-600" /> {t("products.qrScan")}</p>
        <button type="button" onClick={downloadPng} data-testid="detail-qr-download"
          className="mt-2 inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2.5 py-1 text-xs font-medium text-slate-600 transition-colors hover:border-blue-300 hover:bg-blue-50">
          <Download className="h-3.5 w-3.5" /> {t("products.qrDownload")}
        </button>
      </div>
    </div>
  );
}

function ProductDetailDialog({ product, onClose, onDownload, imgUrl, t, products, onOpen, onOpenGroup }) {
  const [active, setActive] = useState(0);
  const [activeVid, setActiveVid] = useState(0);
  useEffect(() => { setActive(0); setActiveVid(0); }, [product]);
  if (!product) return null;
  const imgs = product.images || [];
  const videos = (product.videos || []).map((v) => ({ ...v, embed: ytEmbed(v.url) })).filter((v) => v.embed);
  const vidLabel = { montaje: t("adminProducts.vidMontaje"), mantenimiento: t("adminProducts.vidMantenimiento"), demo: t("adminProducts.vidDemo"), otro: t("adminProducts.vidOtro") };
  const related = (products || []).filter((x) => x.group === product.group && x.id !== product.id).slice(0, 4);
  return (
    <Dialog open={!!product} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="product-detail-dialog">
        <DialogHeader>
          <nav className="flex items-center gap-1 text-xs text-slate-400" data-testid="detail-breadcrumb">
            <span>{t("products.home")}</span>
            <span>›</span>
            <button type="button" onClick={() => onOpenGroup && onOpenGroup(product.group)} className="hover:text-blue-600" data-testid="breadcrumb-group">{t(`products.group.${product.group}`)}</button>
            <span>›</span>
            <span className="font-medium text-slate-600">{product.name}</span>
          </nav>
          <DialogTitle>{product.name}</DialogTitle>
          <DialogDescription className="sr-only">{product.name}</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <div>
            <div className="flex h-56 items-center justify-center rounded-lg bg-white">
              {imgs.length > 0 ? (
                <img src={imgUrl(product.id, imgs[active].id)} alt={product.name} className="h-full w-full object-contain p-4" data-testid="detail-main-image" />
              ) : (
                <Package className="h-12 w-12 text-slate-300" />
              )}
            </div>
            {imgs.length > 1 && (
              <div className="mt-3 flex flex-wrap gap-2" data-testid="detail-thumbs">
                {imgs.map((im, i) => (
                  <button key={im.id} type="button" onClick={() => setActive(i)}
                    className={`h-14 w-14 overflow-hidden rounded border ${i === active ? "border-blue-500 ring-2 ring-blue-200" : "border-slate-200"}`}>
                    <img src={imgUrl(product.id, im.id)} alt="" className="h-full w-full object-contain" />
                  </button>
                ))}
              </div>
            )}
          </div>
          <div>
            <p className="text-sm leading-relaxed text-slate-600">{product.description}</p>
            {product.reference && (
              <p className="mt-2 text-xs text-slate-500" data-testid="detail-reference">
                <span className="font-semibold uppercase tracking-wide">{t("products.reference")}:</span> {product.reference}
              </p>
            )}
            <button type="button" onClick={async () => {
              const url = `${window.location.origin}/p/${product.id}`;
              try {
                if (navigator.share) await navigator.share({ title: product.name, url });
                else { await navigator.clipboard.writeText(url); toast.success(t("products.shareCopied")); }
              } catch { /* cancelled */ }
            }} data-testid="detail-share"
              className="mt-3 inline-flex items-center gap-1.5 rounded-md border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 transition-colors hover:border-blue-300 hover:bg-blue-50">
              <Share2 className="h-3.5 w-3.5" /> {t("products.share")}
            </button>
            <QrShare url={`${window.location.origin}/p/${product.id}`} name={product.name} t={t} />
            {product.specs && product.specs.length > 0 && (
              <div className="mt-4">
                <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">{t("products.specs")}</h4>
                <table className="w-full text-sm" data-testid="detail-specs">
                  <tbody>
                    {product.specs.map((s, i) => (
                      <tr key={i} className="border-b border-slate-100 last:border-0">
                        <td className="py-1.5 pr-3 text-slate-500">{s.label}</td>
                        <td className="py-1.5 text-right font-medium text-slate-800">{s.value}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {videos.length > 0 && (
          <div className="mt-2" data-testid="detail-video">
            <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">{t("products.video")}</h4>
            {videos.length > 1 && (
              <div className="mb-2 flex flex-wrap gap-1.5" data-testid="detail-video-tabs">
                {videos.map((v, i) => (
                  <button key={i} type="button" onClick={() => setActiveVid(i)}
                    className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${i === activeVid ? "border-blue-500 bg-blue-50 text-blue-700" : "border-slate-200 text-slate-500 hover:bg-slate-50"}`}
                    data-testid={`detail-video-tab-${i}`}>
                    {vidLabel[v.label] || v.label}
                  </button>
                ))}
              </div>
            )}
            <div className="aspect-video w-full overflow-hidden rounded-lg border border-slate-200">
              <iframe src={videos[activeVid].embed} title={product.name} className="h-full w-full" allowFullScreen
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" />
            </div>
          </div>
        )}

        <div className="mt-2">
          <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">{t("products.manualsTitle")}</h4>
          <div className="space-y-1.5">
            {product.manuals.length === 0 && <p className="text-xs text-slate-400">{t("products.noManuals")}</p>}
            {product.manuals.map((m) => (
              <div key={m.id} className="rounded-md border border-slate-200 px-3 py-2" data-testid={`detail-manual-${m.id}`}>
                <div className="flex items-center gap-2 text-sm text-slate-700">
                  <FileText className="h-4 w-4 shrink-0 text-blue-600" />
                  <span className="flex-1 truncate">{m.title}</span>
                </div>
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  {(m.langs || []).map((lg) => (
                    <button key={lg} data-testid={`detail-download-${m.id}-${lg}`}
                      onClick={() => onDownload(product.id, m.id, m.filenames[lg] || `${m.title}.pdf`, lg)}
                      className="inline-flex items-center gap-1 rounded border border-slate-200 px-2 py-1 text-xs font-medium text-slate-600 transition-colors hover:border-blue-300 hover:bg-blue-50">
                      <Download className="h-3 w-3" /> {LANG_LABEL[lg]}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {related.length > 0 && (
          <div className="mt-4 border-t border-slate-100 pt-4">
            <h4 className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">{t("products.related")}</h4>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="related-products">
              {related.map((r) => (
                <button key={r.id} type="button" onClick={() => onOpen(r)} data-testid={`related-${r.id}`}
                  className="flex flex-col overflow-hidden rounded-lg border border-slate-200 text-left transition-shadow hover:shadow-md">
                  <div className="flex h-20 items-center justify-center bg-white">
                    {r.images && r.images.length > 0 ? (
                      <img src={imgUrl(r.id, r.images[0].id)} alt={r.name} className="h-full w-full object-contain p-2" />
                    ) : (
                      <Package className="h-6 w-6 text-slate-300" />
                    )}
                  </div>
                  <span className="truncate p-2 text-xs font-medium text-slate-700">{r.name}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
